import { groundKind, surfaceAt, towardConcrete } from "../level";
import type { Skater } from "../skater";
import { bail } from "./bail";
import { wrapAngle } from "./math";
import type { SkaterEvent } from "./state";
import { SAND_CRAWL, SAND_DECEL, SAND_FACING, SAND_STOPPED, SAND_TURN } from "./tuning";

// Soft edges (#82): a side of the park can be a beach instead of a wall
// (`Level.edges`). Sand slows you to a stop, with no bail and no wall, then
// turns you back toward the concrete and lets you trudge out. The sea is a
// Wipeout: a bail, then you're back at the last safe spot. Only maps with
// edges get here, so every other map skates exactly as before.

/**
 * Rolling on sand, instead of the auto-push: slows you hard until you've
 * stopped, then turns you toward the concrete and crawls you back out.
 * Returns false off the sand (push as usual).
 */
export function stepSand(sk: Skater, dt: number): boolean {
  if (groundKind(sk.level, sk.x, sk.z) !== "sand") return false;
  const sign = sk.speed >= 0 ? 1 : -1;
  let mag = Math.abs(sk.speed);
  const home = towardConcrete(sk.level, sk.x, sk.z);
  const f = sk.forward;
  const facing = (f.x * home.x + f.z * home.z) * sign;
  const headingOut = facing >= SAND_FACING;
  if (mag > SAND_CRAWL || !headingOut) mag = Math.max(0, mag - SAND_DECEL * dt);
  if (mag < SAND_STOPPED || headingOut) {
    // Turn so the way you're rolling (fakie too) points at the concrete.
    const target = sign > 0 ? Math.atan2(home.x, home.z) : Math.atan2(-home.x, -home.z);
    const d = wrapAngle(target - sk.yaw);
    sk.yaw += Math.max(-SAND_TURN * dt, Math.min(SAND_TURN * dt, d));
    if (headingOut) mag = Math.min(SAND_CRAWL, mag + SAND_CRAWL * dt);
  }
  sk.speed = mag * sign;
  return true;
}

/**
 * After each step on a map with soft edges: remembers the last safe spot
 * (flat concrete floor), wipes out in the sea, and once the Wipeout's bail
 * is over puts you back at that spot.
 */
export function stepShore(sk: Skater, events: SkaterEvent[]): void {
  const kind = groundKind(sk.level, sk.x, sk.z);
  if (sk.mode === "ground" && sk.wipeout) {
    sk.respawn(sk.lastSafe);
    events.push({ type: "respawn" });
    return;
  }
  if (sk.mode === "ground" && kind === "concrete") {
    const s = surfaceAt(sk.level, sk.x, sk.z);
    if (s.h === 0 && s.gx === 0 && s.gz === 0) sk.lastSafe = { x: sk.x, z: sk.z, yaw: sk.yaw };
    return;
  }
  if (kind !== "water" || sk.wipeout || sk.y > 0.05) return;
  if (sk.mode === "ground" || sk.mode === "air") bail(sk, events, true);
  else if (sk.mode !== "bail") return;
  // Into the sea: no sliding on under water.
  sk.wipeout = true;
  sk.vx = sk.vz = 0;
}
